<template>
  <b-modal
    id="modal-mitra-tna-detail"
    ref="modalMitraTnaDetail"
    title="Approve Training Participants"
    size="lg"
    hide-footer
    @show="onModalShow"
    @hidden="resetModal">
    <div v-if="selectedCourse">
      <!-- Course Info Header -->
      <div class="mb-4">
        <h5 class="mb-2">{{ selectedCourse.title }}</h5>
        <p class="text-muted mb-0">{{ selectedCourse.description }}</p>
      </div>

      <!-- Participants Section -->
      <div class="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <h6 class="mb-1">Training Participants</h6>
          <small class="text-muted">Select participants to approve or reject</small>
        </div>
        <div>
          <span class="badge bg-primary">
            {{ selectedParticipants.length }} Selected
          </span>
          <span class="badge bg-light text-muted ml-2">
            {{ participantPagination.total }} Total
          </span>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Status</label>
            <v-select
              v-model="selectedStatus"
              :options="participantStatusOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua status"
              :clearable="true">
            </v-select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Approval</label>
            <v-select
              v-model="selectedApprovalStep"
              :options="approvalStepOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua approval"
              :clearable="true">
            </v-select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Course Type</label>
            <v-select
              v-model="selectedCourseType"
              :options="courseTypeOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua tipe"
              :clearable="true">
            </v-select>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3 text-muted mb-0">Loading participants...</p>
      </div>
      
      <div v-else-if="filteredParticipants.length === 0" class="text-center py-5">
        <i class="fe fe-users" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">No participants found</p>
      </div>

      <!-- Participants Table -->
      <div v-else-if="!loading" class="table-responsive">
        <table class="table table-sm table-hover">
          <thead>
            <tr>
              <th style="width: 40px;">
                <div class="custom-control custom-checkbox">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="selectAll"
                    :checked="isAllSelected"
                    @change="toggleSelectAll">
                  <label class="custom-control-label" for="selectAll"></label>
                </div>
              </th>
              <th style="width: 40px;">No</th>
              <th>Employee Name</th>
              <th>Employee ID</th>
              <th>Jabatan</th>
              <th>Company</th>
              <th>Year</th>
              <!-- <th>Email</th> -->
              <th>Approval</th>
              <th>Status</th>
              <!-- <th>Submitted At</th> -->
            </tr>
          </thead>
          <tbody>
            <tr v-for="(participant, index) in filteredParticipants" :key="participant.id">
              <td>
                <div class="custom-control custom-checkbox">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    :id="`participant-${participant.id}`"
                    :value="participant.id"
                    v-model="selectedParticipants"
                    :disabled="!participant.is_actionable">
                  <label class="custom-control-label" :for="`participant-${participant.id}`"></label>
                </div>
              </td>
              <td>{{ (participantPagination.page - 1) * participantPagination.limit + index + 1 }}</td>
              <td>
                {{ participant.employee_name }}
                <span
                  class="badge ms-1"
                  :class="participant.is_mandatory ? 'badge-soft-info' : 'badge-soft-secondary'"
                  style="font-size: 0.65rem;">
                  {{ participant.training_requirement || (participant.is_mandatory ? 'Mandatory' : 'Development') }}
                </span>
              </td>
              <td>
                <span class="badge badge-soft-primary">{{ participant.employee_id }}</span>
              </td>
              <td>{{ participant.position }}</td>
              <td>
                <small class="text-muted">{{ participant.company || '-' }}</small>
              </td>
              <td>
                <span class="badge badge-soft-info">{{ participant.year }}</span>
              </td>
              <!-- <td>{{ participant.email }}</td> -->
              <td>
                <span class="badge badge-soft-secondary">{{ participant.approval_step_label || '-' }}</span>
              </td>
              <td>
                <span 
                  class="badge" 
                  :class="getParticipantStatusBadgeClass(participant.status)">
                  {{ participant.status }}
                </span>
              </td>
              <!-- <td>
                <small class="text-muted">{{ participant.submitted_at }}</small>
              </td> -->
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="participantPagination.totalPage > 1" class="mt-3 pt-3 border-top">
        <div class="row align-items-center">
          <div class="col-md-4">
            <div class="d-flex align-items-center">
              <span class="text-muted small mr-2">Show</span>
              <select
                v-model.number="participantPagination.limit"
                class="form-control form-control-sm"
                style="width: 80px;"
                @change="onParticipantPerPageChange"
              >
                <option v-for="option in participantPerPageOptions" :key="option" :value="option">{{ option }}</option>
              </select>
              <span class="text-muted small ml-2">entries</span>
            </div>
          </div>
          <div class="col-md-4 text-center">
            <small class="text-muted">
              Showing {{ participantStart }} to {{ participantEnd }} of {{ participantPagination.total }} entries
            </small>
          </div>
          <div class="col-md-4">
            <nav>
              <ul class="pagination pagination-sm justify-content-end mb-0">
                <li class="page-item" :class="{ disabled: participantPagination.page === 1 }">
                  <button class="page-link" @click="changeParticipantPage(participantPagination.page - 1)" :disabled="participantPagination.page === 1">
                    <i class="fe fe-chevron-left"></i>
                  </button>
                </li>
                <li
                  v-for="page in participantVisiblePages"
                  :key="page"
                  class="page-item"
                  :class="{ active: participantPagination.page === page, disabled: page === '...' }"
                >
                  <button v-if="page === '...'" class="page-link" disabled>...</button>
                  <button v-else class="page-link" @click="changeParticipantPage(page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: participantPagination.page === participantPagination.totalPage }">
                  <button class="page-link" @click="changeParticipantPage(participantPagination.page + 1)" :disabled="participantPagination.page === participantPagination.totalPage">
                    <i class="fe fe-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div v-if="hasPendingParticipants" class="mt-4 d-flex gap-2 justify-content-end">
        <button
          v-if="canApproveAll"
          type="button"
          class="btn btn-outline-success"
          @click="approveAllParticipants"
        >
          <i class="fe fe-check-circle me-2"></i>
          Approve All ({{ participantPagination.total }})
        </button>
        <button 
          type="button" 
          class="btn btn-success"
          :disabled="selectedParticipants.length === 0"
          @click="approveParticipants">
          <i class="fe fe-check me-2"></i>
          Approve
        </button>
        <button 
          type="button" 
          class="btn btn-danger"
          :disabled="selectedParticipants.length === 0"
          @click="rejectParticipants">
          <i class="fe fe-x me-2"></i>
          Reject
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { ROLE_HSE, ROLE_ADMIN_PJO } from '@/constants/mitraRole';
import {
  APPROVAL_STEP_SLUG_OPTIONS,
  COURSE_TYPE_OPTIONS,
  PARTICIPANT_STATUS_OPTIONS,
  buildMitraFilterParams
} from '@/constants/mitraFilters';

export default {
  name: 'ModalMitraTnaDetail',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    selectedCompany: {
      type: String,
      default: null
    },
    selectedPeriod: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      selectedParticipants: [],
      participants: [],
      loading: false,
      selectedStatus: null,
      selectedApprovalStep: null,
      selectedCourseType: null,
      participantStatusOptions: PARTICIPANT_STATUS_OPTIONS,
      approvalStepOptions: APPROVAL_STEP_SLUG_OPTIONS,
      courseTypeOptions: COURSE_TYPE_OPTIONS,
      participantPerPageOptions: [25, 50, 100],
      participantPagination: {
        page: 1,
        limit: 25,
        total: 0,
        totalPage: 1
      }
    };
  },
  computed: {
    ...mapGetters('users', ['profileData']),
    filteredParticipants() {
      let participantsList = this.participants;
      
      if (this.selectedCompany) {
        participantsList = participantsList.filter(p => p.company === this.selectedCompany);
      }
      
      return participantsList;
    },
    hasPendingParticipants() {
      return this.filteredParticipants.some(p => p.is_actionable);
    },
    isAllSelected() {
      const pendingParticipants = this.filteredParticipants.filter(p => p.is_actionable);
      return pendingParticipants.length > 0 && 
             this.selectedParticipants.length === pendingParticipants.length;
    },
    canApproveAll() {
      const userRoles = this.profileData?.roles_uenroll || [];
      return userRoles.includes(ROLE_ADMIN_PJO) || userRoles.includes(ROLE_HSE);
    },
    participantStart() {
      if (!this.participantPagination.total) return 0;
      return (this.participantPagination.page - 1) * this.participantPagination.limit + 1;
    },
    participantEnd() {
      return Math.min(this.participantPagination.page * this.participantPagination.limit, this.participantPagination.total);
    },
    participantVisiblePages() {
      const total = this.participantPagination.totalPage || 1;
      const current = this.participantPagination.page || 1;
      const pages = [];

      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }

      pages.push(1);
      if (current > 3) pages.push('...');

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (current < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    }
  },
  watch: {
    selectedCourse: {
      immediate: false,
      async handler(course) {
        if (course && course.id && this.$refs.modalMitraTnaDetail && this.$refs.modalMitraTnaDetail.isVisible) {
          await this.loadParticipants();
        }
      }
    },
    selectedApprovalStep() {
      this.reloadParticipantsFromFirstPage();
    },
    selectedCourseType() {
      this.reloadParticipantsFromFirstPage();
    },
    selectedStatus() {
      this.reloadParticipantsFromFirstPage();
    }
  },
  methods: {
    ...mapActions('mitraTnaReport', {
      getSubmittedParticipantsAction: 'getSubmittedParticipants',
      approveParticipantsAction: 'approveParticipants',
      rejectParticipantsAction: 'rejectParticipants'
    }),
    
    async onModalShow() {
      if (this.selectedCourse && this.selectedCourse.id) {
        await this.loadParticipants();
      }
    },
    
    async loadParticipants() {
      if (!this.selectedCourse || !this.selectedCourse.id) return;
      
      this.loading = true;
      try {
        const params = {};
        if (this.selectedCompany) params.company = this.selectedCompany;
        params.page = this.participantPagination.page;
        params.limit = this.participantPagination.limit;
        params.tna_period = this.selectedCourse.tna_period_id || this.selectedPeriod;
        Object.assign(params, buildMitraFilterParams({
          status: this.selectedStatus,
          approval_step: this.selectedApprovalStep,
          course_type: this.selectedCourseType
        }, { includeParticipantStatusFromApproval: true }));

        const result = await this.getSubmittedParticipantsAction({
          courseId: this.selectedCourse.id,
          params
        });
        this.participants = result.data || [];
        this.participantPagination = {
          page: result.page || this.participantPagination.page,
          limit: result.limit || this.participantPagination.limit,
          total: result.total || 0,
          totalPage: result.totalPage || 1
        };
        this.selectedParticipants = [];
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: error.message || 'Peserta belum berhasil dimuat',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.loading = false;
      }
    },
    
    resetModal() {
      this.selectedParticipants = [];
      this.participants = [];
      this.selectedStatus = null;
      this.selectedApprovalStep = null;
      this.selectedCourseType = null;
      this.participantPagination = {
        page: 1,
        limit: this.participantPagination.limit,
        total: 0,
        totalPage: 1
      };
    },
    reloadParticipantsFromFirstPage() {
      if (!this.$refs.modalMitraTnaDetail || !this.$refs.modalMitraTnaDetail.isVisible) return;
      this.participantPagination.page = 1;
      this.loadParticipants();
    },
    changeParticipantPage(page) {
      if (page < 1 || page > this.participantPagination.totalPage) return;
      this.participantPagination.page = page;
      this.loadParticipants();
    },
    onParticipantPerPageChange() {
      this.participantPagination.page = 1;
      this.loadParticipants();
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        // Select all pending participants (from filtered results)
        this.selectedParticipants = this.filteredParticipants
          .filter(p => p.is_actionable)
          .map(p => p.id);
      } else {
        // Deselect all
        this.selectedParticipants = [];
      }
    },

    getParticipantStatusBadgeClass(status) {
      const normalizedStatus = String(status || '').toLowerCase();
      if (normalizedStatus === 'approved') return 'badge-soft-success';
      if (normalizedStatus === 'rejected' || normalizedStatus === 'cancelled') return 'badge-soft-danger';
      return 'badge-soft-warning';
    },
    
    async approveParticipants() {
      const result = await this.$swal({
        title: 'Setujui Peserta?',
        html: `You are about to approve <strong>${this.selectedParticipants.length}</strong> participant(s).<br/>This action cannot be undone.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, setujui',
        confirmButtonColor: '#00c9a7',
        cancelButtonText: 'Batal',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        try {
          // Get TNA IDs from selected participants
          const tnaIds = this.participants
            .filter(p => this.selectedParticipants.includes(p.id))
            .map(p => Number(p.tna_id));
          
          // Call approve API with role-based routing
          await this.approveParticipantsAction({
            courseId: this.selectedCourse.id,
            tnaIds: tnaIds,
            tnaPeriodId: this.selectedCourse.tna_period_id
          });

          this.$swal({
            title: 'Success',
            text: `${this.selectedParticipants.length} peserta berhasil disetujui.`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });

          // Reload participants to get updated status
          await this.loadParticipants();
          this.selectedParticipants = [];
          
          // Emit event to parent to refresh course list
          this.$emit('refresh');
        } catch (error) {
          this.$swal({
            title: 'Error',
            text: error.message || 'Peserta belum berhasil disetujui',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        }
      }
    },
    async approveAllParticipants() {
      const result = await this.$swal({
        title: 'Setujui Semua Peserta?',
        html: `You are about to approve all <strong>${this.participantPagination.total}</strong> pending participant(s) for this course.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, setujui semua',
        confirmButtonColor: '#00c9a7',
        cancelButtonText: 'Batal',
        reverseButtons: true
      });

      if (!result.isConfirmed) return;

      try {
        await this.approveParticipantsAction({
          courseId: this.selectedCourse.id,
          tnaIds: [],
          approveAll: true,
          tnaPeriodId: this.selectedCourse.tna_period_id
        });

        this.$swal({
          title: 'Success',
          text: 'Semua peserta pending berhasil disetujui.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });

        await this.loadParticipants();
        this.$emit('refresh');
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: error.message || 'Semua peserta belum berhasil disetujui',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    async rejectParticipants() {
      const result = await this.$swal({
        title: 'Tolak Peserta?',
        html: `You are about to reject <strong>${this.selectedParticipants.length}</strong> participant(s).<br/>This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, tolak',
        confirmButtonColor: '#e63757',
        cancelButtonText: 'Batal',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        try {
          // Get TNA IDs from selected participants
          const tnaIds = this.participants
            .filter(p => this.selectedParticipants.includes(p.id))
            .map(p => Number(p.tna_id));
          
          // Call reject API with role-based routing
          await this.rejectParticipantsAction({
            courseId: this.selectedCourse.id,
            tnaIds: tnaIds
          });

          this.$swal({
            title: 'Success',
            text: `${this.selectedParticipants.length} peserta berhasil ditolak.`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });

          // Reload participants to get updated status
          await this.loadParticipants();
          this.selectedParticipants = [];
          
          // Emit event to parent to refresh course list
          this.$emit('refresh');
        } catch (error) {
          this.$swal({
            title: 'Error',
            text: error.message || 'Peserta belum berhasil ditolak',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        }
      }
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.ms-1 {
  margin-left: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}

.badge-soft-primary {
  color: #2c7be5;
  background-color: rgba(44, 123, 229, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.custom-control-input:disabled ~ .custom-control-label {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-responsive {
  max-height: 500px;
  overflow-y: auto;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 1;
}
</style>
